import React, { useEffect, useState } from 'react';
import { getUserById } from '../services/userService';
import { useParams, useNavigate } from 'react-router-dom';
import './ProfilePage.css';
import { auth, storage, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import ListenerProfile from './ListenerProfile';
import ArtistProfile from './ArtistProfile';
import BadgeCollection from './BadgeCollection';
import { observeWalletBalance } from '../services/transactionsService';

const ProfilePage = () => {
    const params = useParams();
    const paramUid = params?.uid;
    const navigate = useNavigate();
    const [profileUid, setProfileUid] = useState(null);
    const [user, setUser] = useState(null);
    const [editing, setEditing] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [editName, setEditName] = useState('');
    const [editPhotoFile, setEditPhotoFile] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [showBadgeCollection, setShowBadgeCollection] = useState(false);

    // ✅ Fix: Resolve profileUid - priority: URL param > auth.currentUser
    useEffect(() => {
        // If URL param exists, use it immediately
        if (paramUid) {
            console.log('Debug: Using URL param uid:', paramUid);
            setProfileUid(paramUid);
            return;
        }
        
        // Otherwise, wait for auth and use current user's uid
        if (auth.currentUser && auth.currentUser.uid) {
            console.log('Debug: Using auth.currentUser.uid:', auth.currentUser.uid);
            setProfileUid(auth.currentUser.uid);
            return;
        }
        
        // Subscribe to auth state changes
        const unsub = onAuthStateChanged(auth, (u) => {
            if (u && u.uid) {
                console.log('Debug: Auth state changed, using uid:', u.uid);
                setProfileUid(u.uid);
            }
        });
        return () => unsub();
    }, [paramUid, navigate]);

    // Listen for global 'open-profile-editor' to open edit modal from child components
    useEffect(() => {
        const openEditor = () => navigate('/edit-profile');
        const openPhotoEditor = () => navigate('/edit-profile?photo=1');
        window.addEventListener('open-profile-editor', openEditor);
        window.addEventListener('open-profile-photo-editor', openPhotoEditor);
        return () => {
            window.removeEventListener('open-profile-editor', openEditor);
            window.removeEventListener('open-profile-photo-editor', openPhotoEditor);
        };
    }, [navigate]);

    // Use real-time listener instead of blocking getUserById
    useEffect(() => {
        if (!profileUid) {
            return () => {};
        }
        
        const userRef = doc(db, 'users', profileUid);
        const unsub = onSnapshot(userRef, (snap) => {
            if (snap.exists()) {
                const userData = { id: snap.id, ...snap.data() };
                setUser(userData);
                if (auth.currentUser && auth.currentUser.uid === profileUid) {
                    setEditName(userData?.name || userData?.displayName || '');
                }
            } else {
                setUser(null);
            }
        }, (err) => {
            console.error('Failed to load profile user', err);
            setUser(null);
        });
        return () => unsub();
    }, [profileUid]);

    // Observe wallet balance for artists
    useEffect(() => {
        if (!profileUid || user?.role !== 'artist' || auth.currentUser?.uid !== profileUid) {
            setWalletBalance(0);
            return () => {};
        }
        const unsub = observeWalletBalance(profileUid, (bal) => {
            setWalletBalance(Number(bal || 0));
        });
        return () => unsub();
    }, [profileUid, user?.role]);

    // Render immediately, show profile components even if user data is loading
    // Profile components will handle their own loading states

    // Always show ListenerProfile if user.role is 'listener' (even if route/uid is wrong)
    return (
        <div className="flex flex-col items-center justify-start min-h-screen overflow-x-hidden">
            {/* Sticky Header */}
            <div className="sticky top-0 z-50 bg-[#000] flex justify-between items-center px-4 py-3 shadow-md w-full">
                {/* HubTune logo/text */}
                <h1 className="text-2xl font-bold text-red-500">HubTune</h1>

                {/* Right section: wallet + icons */}
                <div className="flex items-center space-x-3 relative">
                    {/* Show wallet only for artists - check user safely */}
                    {user && user.role === 'artist' && auth.currentUser?.uid === profileUid && (
                        <div className="bg-gray-800 text-white px-3 py-1 rounded-full text-sm font-medium">
                            Wallet: ₹{Number(walletBalance || 0).toLocaleString('en-IN')}
                        </div>
                    )}
                    {/* Show post and settings buttons only for own profile */}
                    {auth.currentUser && auth.currentUser.uid === profileUid && (
                        <>
                            {/* Post button for artists */}
                            {user && user.role === 'artist' && (
                                <button 
                                    className="text-gray-300 text-xl hover:text-white"
                                    aria-label="Post new song"
                                    onClick={() => window.dispatchEvent(new CustomEvent('open-upload-modal'))}
                                    title="Post Song"
                                >
                                    ➕
                                </button>
                            )}
                            <button 
                                className="text-gray-300 text-xl hover:text-white"
                                aria-label="Open settings"
                                onClick={() => setShowSettings(v => !v)}
                                title="Settings"
                            >
                                ⚙️
                            </button>
                            {showSettings && (
                                <div className="absolute top-10 right-0 bg-[#1a1a1a] border border-gray-700 rounded-lg p-2 min-w-[180px] z-50">
                                    {user?.role === 'artist' && (
                                        <button
                                            className="w-full text-left bg-transparent text-white border-none py-2 px-3 cursor-pointer hover:bg-gray-800 rounded"
                                            onClick={() => { setShowSettings(false); navigate(`/wallet/${profileUid}`); }}
                                        >
                                            Wallet
                                        </button>
                                    )}
                                    {user?.role !== 'artist' && (
                                        <button
                                            className="w-full text-left bg-transparent text-white border-none py-2 px-3 cursor-pointer hover:bg-gray-800 rounded"
                                            onClick={() => { setShowSettings(false); setShowBadgeCollection(true); }}
                                        >
                                            Badge Collection
                                        </button>
                                    )}
                                    <button
                                        className="w-full text-left bg-transparent text-white border-none py-2 px-3 cursor-pointer hover:bg-gray-800 rounded"
                                        onClick={() => { setShowSettings(false); navigate('/edit-profile'); }}
                                    >
                                        Edit Profile
                                    </button>
                                    <button
                                        className="w-full text-left bg-transparent text-white border-none py-2 px-3 cursor-pointer hover:bg-gray-800 rounded"
                                        onClick={async () => { setShowSettings(false); await auth.signOut(); navigate('/'); }}
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {showBadgeCollection && (
                <BadgeCollection
                    uid={profileUid}
                    selectedBadgeId={user?.selectedBadgeId}
                    onClose={() => setShowBadgeCollection(false)}
                />
            )}

            {/* Main content with margin-top to account for sticky header */}
            <div className="mt-[70px] overflow-y-auto w-full">
                <div className="flex justify-center">
                    <div className="w-full max-w-md">
                        {/* Render profile component immediately - it will handle loading internally */}
                        {user && user.role === 'listener' ? (
                            <ListenerProfile uid={profileUid} />
                        ) : (
                            <ArtistProfile uid={profileUid} />
                        )}
                    </div>
                </div>
            </div>

            {/* Edit modal for profile owner */}
            {editing && auth.currentUser && auth.currentUser.uid === profileUid && (
                <div className="modal-overlay" onClick={() => setEditing(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ background: '#111', color: '#fff', padding: 16, borderRadius: 8, width: 480 }}>
                        <h3>Edit profile</h3>
                        <div style={{ marginTop: 8 }}>
                            <label style={{ display: 'block', marginBottom: 6 }}>Display name</label>
                            <input value={editName} onChange={(e) => setEditName(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #333' }} />
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <label style={{ display: 'block', marginBottom: 6 }}>Profile photo</label>
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    setEditPhotoFile(file);
                                    setUploadError(null);
                                    if (file) {
                                        // Validate file size (max 5MB for images)
                                        if (file.size > 5 * 1024 * 1024) {
                                            alert('❌ Image size must be less than 5MB');
                                            setEditPhotoFile(null);
                                            e.target.value = '';
                                            return;
                                        }
                                        // Validate file type
                                        if (!file.type.startsWith('image/')) {
                                            alert('❌ Please select a valid image file');
                                            setEditPhotoFile(null);
                                            e.target.value = '';
                                            return;
                                        }
                                        console.log('Photo selected:', file.name, file.size, 'bytes');
                                    }
                                }} 
                                disabled={uploadingPhoto}
                            />
                            {editPhotoFile && (
                                <p style={{ marginTop: 4, fontSize: '0.9rem', color: '#aaa' }}>
                                    Selected: {editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)
                                </p>
                            )}
                            {uploadError && (
                                <p style={{ marginTop: 4, fontSize: '0.9rem', color: '#ff4444' }}>
                                    {uploadError}
                                </p>
                            )}
                            {uploadingPhoto && (
                                <p style={{ marginTop: 4, fontSize: '0.9rem', color: '#4CAF50' }}>
                                    Uploading photo...
                                </p>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => setEditing(false)} className="btn secondary">Cancel</button>
                            {user?.role === 'artist' && (
                                <button
                                    onClick={() => {
                                        setEditing(false);
                                        navigate('/setup-artist-upi');
                                    }}
                                    className="btn secondary"
                                    style={{ borderColor: '#ff3b3b', color: '#ff8a8a' }}
                                >
                                    Edit UPI ID
                                </button>
                            )}
                            <button
                                onClick={async () => {
                                    try {
                                        setUploadError(null);
                                        setUploadingPhoto(false);
                                        
                                        const updates = {};
                                        
                                        // Update name if changed
                                        if (editName && editName.trim() !== (user.name || user.displayName || '')) {
                                            updates.name = editName.trim();
                                        }
                                        
                                        // Upload photo if selected
                                        if (editPhotoFile) {
                                            // Check internet connection first
                                            if (!navigator.onLine) {
                                                alert('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
                                                return;
                                            }

                                            // Check if user is authenticated
                                            if (!auth.currentUser) {
                                                alert('❌ You must be logged in to upload photos. Please sign in and try again.');
                                                return;
                                            }

                                            // Verify profileUid matches current user
                                            if (auth.currentUser.uid !== profileUid) {
                                                alert('❌ Authentication error. Please refresh the page and try again.');
                                                return;
                                            }

                                            setUploadingPhoto(true);
                                            try {
                                                // Sanitize file name - remove special characters and spaces
                                                const sanitizeFileName = (fileName) => {
                                                    // Get file extension
                                                    const ext = fileName.substring(fileName.lastIndexOf('.'));
                                                    // Get file name without extension
                                                    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
                                                    // Replace special characters with underscores and remove spaces
                                                    const sanitized = nameWithoutExt
                                                        .replace(/[^a-zA-Z0-9]/g, '_')
                                                        .replace(/_+/g, '_')
                                                        .replace(/^_|_$/g, '');
                                                    return sanitized + ext;
                                                };
                                                
                                                const sanitizedFileName = sanitizeFileName(editPhotoFile.name);
                                                const path = `avatars/${profileUid}/${Date.now()}_${sanitizedFileName}`;
                                                console.log('Uploading photo to path:', path);
                                                const sRef = storageRef(storage, path);
                                                
                                                // Verify authentication before upload
                                                if (!auth.currentUser) {
                                                    throw new Error('❌ You must be logged in to upload photos. Please sign in and try again.');
                                                }
                                                
                                                // Verify auth token is valid
                                                try {
                                                    await auth.currentUser.getIdToken(true);
                                                } catch (authErr) {
                                                    console.error('Auth token error:', authErr);
                                                    throw new Error('❌ Authentication expired. Please refresh the page and try again.');
                                                }
                                                
                                                // Use resumable upload for better reliability
                                                const uploadTask = uploadBytesResumable(sRef, editPhotoFile, {
                                                    contentType: editPhotoFile.type || 'image/png',
                                                    customMetadata: {
                                                        uploadedBy: auth.currentUser.uid,
                                                        uploadedAt: new Date().toISOString()
                                                    }
                                                });
                                                
                                                // Wait for upload to complete with longer timeout for better reliability
                                                const UPLOAD_TIMEOUT = 300000; // 5 minutes timeout for photos (allows for slow connections)
                                                let uploadCompleted = false;
                                                let timeoutId = null;
                                                
                                                const url = await new Promise((resolve, reject) => {
                                                    // Set timeout to prevent hanging - but don't cancel immediately
                                                    timeoutId = setTimeout(() => {
                                                        if (!uploadCompleted) {
                                                            // Check if upload is still in progress
                                                            const taskState = uploadTask.snapshot?.state;
                                                            if (taskState === 'running' || taskState === 'paused') {
                                                                // Upload is still active, give it more time
                                                                console.warn('Upload taking longer than expected, but still in progress...');
                                                                // Reset timeout for another 2 minutes
                                                                timeoutId = setTimeout(() => {
                                                                    if (!uploadCompleted) {
                                                                        uploadTask.cancel();
                                                                        reject(new Error('Upload timeout: The upload took too long. Please check your internet connection and try again with a smaller file.'));
                                                                    }
                                                                }, 120000); // Additional 2 minutes
                                                            } else {
                                                                // Upload seems stuck, cancel it
                                                                uploadTask.cancel();
                                                                reject(new Error('Upload timeout: Please check your internet connection and try again.'));
                                                            }
                                                        }
                                                    }, UPLOAD_TIMEOUT);
                                                    
                                                    uploadTask.on(
                                                        'state_changed',
                                                        (snapshot) => {
                                                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                                            console.log(`Photo upload progress: ${Math.round(progress)}% (${(snapshot.bytesTransferred / 1024).toFixed(2)} KB / ${(snapshot.totalBytes / 1024).toFixed(2)} KB)`);
                                                            
                                                            // Reset timeout on progress - upload is active
                                                            if (timeoutId) {
                                                                clearTimeout(timeoutId);
                                                                timeoutId = setTimeout(() => {
                                                                    if (!uploadCompleted) {
                                                                        uploadTask.cancel();
                                                                        reject(new Error('Upload timeout: Please check your internet connection and try again.'));
                                                                    }
                                                                }, UPLOAD_TIMEOUT);
                                                            }
                                                        },
                                                        (error) => {
                                                            if (timeoutId) clearTimeout(timeoutId);
                                                            console.error('Photo upload error:', error);
                                                            console.error('Error code:', error.code);
                                                            console.error('Error message:', error.message);
                                                            
                                                            // Don't reject if it's a cancellation we initiated
                                                            if (error.code === 'storage/canceled' && uploadCompleted) {
                                                                return; // Already handled
                                                            }
                                                            
                                                            // Check if offline
                                                            if (!navigator.onLine) {
                                                                reject(new Error('📡 No Internet Connection\n\nPlease check your internet connection and try again.'));
                                                                return;
                                                            }
                                                            
                                                            let errorMessage = 'Photo upload failed. ';
                                                            if (error.code === 'storage/unauthorized') {
                                                                errorMessage += '❌ Permission denied. Please check Firebase Storage rules are deployed and CORS is configured.';
                                                            } else if (error.code === 'storage/canceled') {
                                                                // Only show cancellation error if it wasn't due to timeout
                                                                if (!errorMessage.includes('timeout')) {
                                                                    errorMessage += 'Upload was canceled.';
                                                                }
                                                            } else if (error.code === 'storage/unknown' || error.code === 'storage/retry-limit-exceeded') {
                                                                errorMessage += '📡 Network error. This might be a CORS issue. Please ensure CORS is configured on your storage bucket.';
                                                            } else if (error.code === 'storage/unauthenticated') {
                                                                errorMessage += '❌ Authentication failed. Please refresh the page and sign in again.';
                                                            } else if (error.message && (error.message.includes('ERR_FAILED') || error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('CORS') || error.message.includes('CORS policy'))) {
                                                                errorMessage += '📡 CORS or network error. Please ensure:\n1. CORS is configured on your storage bucket\n2. Storage rules are deployed\n3. You have a stable internet connection';
                                                            } else {
                                                                errorMessage += error.message || 'Please try again.';
                                                            }
                                                            reject(new Error(errorMessage));
                                                        },
                                                        async () => {
                                                            // Upload completed successfully
                                                            try {
                                                                if (timeoutId) clearTimeout(timeoutId);
                                                                uploadCompleted = true;
                                                                console.log('Getting photo download URL...');
                                                                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                                                console.log('✅ Photo download URL obtained:', downloadURL);
                                                                resolve(downloadURL);
                                                            } catch (err) {
                                                                if (timeoutId) clearTimeout(timeoutId);
                                                                console.error('Error getting photo download URL:', err);
                                                                reject(new Error('Failed to get download URL: ' + (err.message || 'Unknown error')));
                                                            }
                                                        }
                                                    );
                                                });
                                                
                                                updates.photoURL = url;
                                                setUploadingPhoto(false);
                                                console.log('✅ Profile photo uploaded successfully:', url);
                                            } catch (photoErr) {
                                                console.error('Photo upload failed:', photoErr);
                                                // Check if offline
                                                if (!navigator.onLine) {
                                                    setUploadError('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
                                                    setUploadingPhoto(false);
                                                    alert('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
                                                } else {
                                                    const errorMsg = photoErr.message || 'Failed to upload photo';
                                                    setUploadError(errorMsg);
                                                    setUploadingPhoto(false);
                                                    alert(`❌ ${errorMsg}`);
                                                }
                                                return; // Don't proceed if photo upload fails
                                            }
                                        }
                                        
                                        // Update Firestore if there are changes
                                        if (Object.keys(updates).length > 0) {
                                            const uRef = doc(db, 'users', profileUid);
                                            await updateDoc(uRef, updates);
                                            console.log('✅ Profile updated successfully');
                                            
                                            // Refresh user data - onSnapshot will auto-update, but we can also manually refresh
                                            const refreshed = await getUserById(profileUid);
                                            setUser(refreshed);
                                            
                                            alert('✅ Profile updated successfully!');
                                        } else {
                                            alert('No changes to save');
                                        }
                                        
                                        setEditing(false);
                                        setEditPhotoFile(null); // Clear file input
                                    } catch (err) {
                                        console.error('Failed to save profile edits', err);
                                        setUploadError(err.message || 'Failed to save changes');
                                        alert(`❌ Failed to save changes: ${err.message || 'Please try again'}`);
                                        setUploadingPhoto(false);
                                    }
                                }}
                                className="btn primary"
                                disabled={uploadingPhoto}
                            >
                                {uploadingPhoto ? 'Uploading...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;
